/* eslint-disable no-console */
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { userSelector } from 'Redux/LoggedUserSlice';

import { S3_DOCUMENTS, S3_EVIDENCES } from 'Constants/General';
import useDocuments from './useDocuments';
import useEvaluations from './useEvaluations';

/**
 * Hook para manejar la pestaña de documentos en la página de detalles de un curso.
 *
 * @param {string} courseId - Id del curso.
 * @returns {{
 *   documents: Array<{key: string, size: number, fileName: string}>,
 *   loading: boolean,
 *   error: string | undefined | null,
 *   fetchDocumentsAndEvaluations: (docsKey: string, evalsKey: string) => Promise<Array<{key: string, size: number, fileName: string}>>
 * }}
 */
const useDocumentsTab = courseId => {
  /** @type {Array<{key: string, size: number, fileName: string}[]>} */
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Redux selector para obtener el id del usuario.
  const { id } = useSelector(userSelector);

  // Llaves para obtener los documentos y evaluaciones del curso.
  const documentsKey = `${S3_DOCUMENTS}/${courseId}`;
  const evaluationsKey = `${S3_EVIDENCES}/${id}/${courseId}`;

  // Hook para traer todos los documentos del curso.
  const { fetchDocuments } = useDocuments();

  // Hook para obtener todas las evaluaciones del usuario de un curso.
  const { fetchEvaluations } = useEvaluations();

  /**
   * Obtiene los documentos del curso y las evaluaciones del usuario.
   * @param {string|null|undefined} documentsKey - Llave para obtener los documentos de un curso en S3.
   * @param {string|null|undefined} evaluationsKey - Llave para obtener las evaluaciones de un usuario en S3.
   * @returns {Promise<typeof documents>} Retorna una promesa con los documentos y evaluaciones.
   */
  const fetchDocumentsAndEvaluations = async (docsKey, evalsKey) => {
    // Quitamos el error
    setError(null);

    /** @type {typeof defaultValue} */
    // Creamos una variable para almacenar todos los documentos actuales.
    let allDocuments = documents;
    // Creamos la variable para almacenar el error.
    let errorMessage = null;

    // > 1. Traemos todos los documentos primero.
    try {
      // Si no se proporciona la llave de los documentos, se lanza un error.
      if (!docsKey) {
        throw new Error('No document key provided');
      }
      // Mostramos indicador de carga.
      setLoading(true);
      // Se obtienen primero los documentos del curso.
      const docs = await fetchDocuments(docsKey);
      // Se agregan los documentos al arreglo de todos los archivos.
      allDocuments = allDocuments.concat(docs.files);
    } catch (caught) {
      console.error('Error fetching documents: ', caught);
      errorMessage = caught;
    }

    // > 2. Traemos todas las evaluaciones del usuario para este curso.
    try {
      // Si no se proporciona la llave de las evaluaciones, se lanza un error.
      if (!evalsKey) {
        throw new Error('No evaluation key provided');
      }
      // Mostramos el indicador de carga.
      setLoading(true);
      // Se obtienen las evaluaciones del usuario.
      const evals = await fetchEvaluations(evalsKey);
      // Se agregan todas las evaluaciones al arreglo de todos los archivos.
      allDocuments = allDocuments.concat(evals.files);
    } catch (caught) {
      console.error('Error fetching evaluations: ', caught);
      // Si ya hay un "errorMessage", entonces concatenamos el nuevo error.
      // Si no hay un "errorMessage", entonces solo mostramos el nuevo error.
      errorMessage = errorMessage ? `${errorMessage} - ${caught}` : caught;
    } finally {
      // Quitamos el cargando
      setLoading(false);
    }

    // Se actualiza el estado de los documentos.
    setDocuments(allDocuments);
    // Se actualiza el estado del error (si hay).
    setError(errorMessage);
    // Se retornan todos los documentos (Por si los necesita sin el update).
    return allDocuments;
  };

  /**
   * Se ejecuta al inicio del hook si hay un courseId y ademas un id.
   */
  useEffect(() => {
    // Es necesario que haya un "courseId" y un "id" (userId) para traer los documentos y evaluaciones del usuario.
    // ! SI NO SE PROPORCIONA TRAERA LOS OBJETOS DEL "PATH" ROOT, QUE NO SON DOCUMENTOS NI EVALUACIONES.
    if (courseId && id) {
      fetchDocumentsAndEvaluations(documentsKey, evaluationsKey);
    }
  }, [courseId, id]);

  return {
    error,
    loading,
    documents,
    fetchDocumentsAndEvaluations,
  };
};

export default useDocumentsTab;
